const isEmpty = value => value === null || value === undefined || String(value).trim() === ''

const ruleMessages = {
  required: name => `${name} is required`,
  email: name => `${name} must be a valid email`,
  password: name => `${name} must contain at least one uppercase, one lowercase, one special character and one digit`,
  confirmed: name => `${name} does not match`,
}

const validators = {
  required: value => !isEmpty(value),
  email: value => isEmpty(value) || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value)),
  password: value => isEmpty(value) || /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).+$/.test(String(value)),
  confirmed: (value, targetValue) => value === targetValue,
}

export const ValidationObserver = {
  name: 'ValidationObserver',
  provide() {
    return {
      validationObserver: this,
    }
  },
  data() {
    return {
      providers: [],
    }
  },
  methods: {
    register(provider) {
      if (!this.providers.includes(provider)) {
        this.providers.push(provider)
      }
    },
    unregister(provider) {
      this.providers = this.providers.filter(item => item !== provider)
    },
    findProvider(id) {
      return this.providers.find(provider => provider.vid === id || provider.name === id)
    },
    async validate() {
      const results = this.providers.map(provider => provider.validate())
      const resolved = await Promise.all(results)
      return resolved.every(Boolean)
    },
    reset() {
      this.providers.forEach(provider => provider.reset())
    },
  },
  render(h) {
    const slot = this.$scopedSlots.default
      ? this.$scopedSlots.default({ invalid: false, validate: this.validate })
      : this.$slots.default

    return h('div', { staticClass: 'validation-observer' }, slot)
  },
}

export const ValidationProvider = {
  name: 'ValidationProvider',
  inject: {
    validationObserver: {
      default: null,
    },
  },
  props: {
    name: {
      type: String,
      default: 'Field',
    },
    rules: {
      type: [String, Object],
      default: '',
    },
    vid: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      errors: [],
    }
  },
  mounted() {
    if (this.validationObserver) {
      this.validationObserver.register(this)
    }
  },
  beforeDestroy() {
    if (this.validationObserver) {
      this.validationObserver.unregister(this)
    }
  },
  methods: {
    getValue() {
      const field = this.$el.querySelector('input, textarea, select')
      return field ? field.value : ''
    },
    parseRules() {
      if (typeof this.rules !== 'string') {
        return Object.keys(this.rules).filter(key => this.rules[key])
      }

      return this.rules.split('|').filter(Boolean)
    },
    getTargetValue(target) {
      const provider = this.validationObserver && this.validationObserver.findProvider(target)
      return provider ? provider.getValue() : ''
    },
    validate() {
      const value = this.getValue()
      const failedRule = this.parseRules().find(rule => {
        const [name, param] = rule.split(':')
        const validator = validators[name]

        if (!validator) {
          return false
        }

        return !validator(value, this.getTargetValue(param))
      })

      this.errors = failedRule ? [ruleMessages[failedRule.split(':')[0]](this.name)] : []
      return this.errors.length === 0
    },
    reset() {
      this.errors = []
    },
  },
  render(h) {
    const slot = this.$scopedSlots.default
      ? this.$scopedSlots.default({ errors: this.errors })
      : this.$slots.default

    return h('div', {
      staticClass: 'validation-provider',
      on: {
        input: this.reset,
        change: this.reset,
      },
    }, slot)
  },
}
